import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useChatStore } from "@/store/chat";
import type { Snapshot } from "../types";

interface ViewportOptions {
  data?: Snapshot;
  dataUpdatedAt: number;
  memberId: string;
  open: boolean;
  online: boolean;
  searching: boolean;
  pendingCount: number;
  receiptPending: boolean;
  boundaryReady: boolean;
  acknowledge: (body: { lastGot: string; lastRead?: string }) => void;
}

/** Scroll the transcript alone: scrollIntoView also scrolls the host page,
 * which a phone keyboard then leaves showing behind the panel. */
export function scrollWithin(
  container: HTMLElement | null,
  target: HTMLElement | null,
  block: "start" | "end",
) {
  if (!container || !target) return;
  const box = container.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  container.scrollTop +=
    block === "start" ? rect.top - box.top : rect.bottom - box.bottom;
}

/** Read receipts are tied to the real scroll viewport, never to polling alone. */
export function useChatViewport({
  data,
  dataUpdatedAt,
  memberId,
  open,
  online,
  searching,
  pendingCount,
  receiptPending,
  acknowledge,
  boundaryReady,
}: ViewportOptions) {
  const [atLatest, setAtLatest] = useState(true);
  const [newestVisible, setNewestVisible] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(
    document.visibilityState === "visible",
  );
  const transcript = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const scrollRevision = useRef(0);
  const jumping = useRef(false);
  const jumpFrame = useRef<number | undefined>(undefined);
  const jumpToLatest = useCallback(() => {
    scrollRevision.current += 1;
    jumping.current = true;
    if (jumpFrame.current !== undefined)
      cancelAnimationFrame(jumpFrame.current);
    const snap = () => {
      const node = transcript.current;
      if (node) node.scrollTo({ top: node.scrollHeight, behavior: "instant" });
    };
    snap();
    setAtLatest(true);
    // The jump button leaves the layout when atLatest changes. Snap again
    // after that render; ignore intermediate scroll events and stale anchors.
    jumpFrame.current = requestAnimationFrame(() => {
      snap();
      jumpFrame.current = requestAnimationFrame(() => {
        snap();
        jumping.current = false;
        jumpFrame.current = undefined;
      });
    });
  }, []);
  useEffect(
    () => () => {
      if (jumpFrame.current !== undefined)
        cancelAnimationFrame(jumpFrame.current);
    },
    [],
  );
  const acknowledged = useRef("");
  const previousNotice = useRef<string | null>(null);
  const latest = data?.messages.at(-1);
  const lastGot =
    data?.messages.reduce(
      (maximum, message) =>
        BigInt(message.eventNo) > BigInt(maximum) ? message.eventNo : maximum,
      "0",
    ) ?? "0";
  const lastViewed =
    data?.messages.reduce(
      (maximum, message) =>
        BigInt(message.noticeSeq) > BigInt(maximum)
          ? message.noticeSeq
          : maximum,
      "0",
    ) ?? "0";

  useEffect(() => {
    const visibility = () =>
      setDocumentVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", visibility);
    return () => document.removeEventListener("visibilitychange", visibility);
  }, []);
  useEffect(() => {
    const target = bottom.current;
    if (!target || !transcript.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setNewestVisible(entry.isIntersecting),
      { root: transcript.current, threshold: 1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [searching]);
  useEffect(() => {
    if (!open || !atLatest || searching || data?.historyWindow) return;
    const frame = requestAnimationFrame(() => {
      const node = transcript.current;
      if (node)
        node.scrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
    });
    return () => cancelAnimationFrame(frame);
  }, [
    open,
    atLatest,
    searching,
    latest?.rootId,
    data?.messages.length,
    pendingCount,
    data?.historyWindow,
  ]);
  useEffect(() => {
    if (!data || !latest || receiptPending || !online) return;
    const read =
      open &&
      atLatest &&
      boundaryReady &&
      documentVisible &&
      newestVisible &&
      !searching &&
      !data.historyWindow
        ? lastViewed
        : undefined;
    if (
      BigInt(data.lastGot) >= BigInt(lastGot) &&
      (!read || BigInt(data.lastRead) >= BigInt(read))
    )
      return;
    const token = `${lastGot}:${read ?? ""}:${dataUpdatedAt}`;
    if (acknowledged.current === token) return;
    acknowledged.current = token;
    acknowledge({ lastGot, ...(read ? { lastRead: read } : {}) });
  }, [
    data,
    latest,
    lastGot,
    lastViewed,
    receiptPending,
    online,
    open,
    atLatest,
    boundaryReady,
    documentVisible,
    newestVisible,
    searching,
    dataUpdatedAt,
    acknowledge,
  ]);
  useEffect(() => {
    if (!data) return;
    const previous = previousNotice.current;
    previousNotice.current = data.lastNoticeSeq;
    if (
      !previous ||
      data.muted ||
      open ||
      !documentVisible ||
      BigInt(data.lastNoticeSeq) <= BigInt(previous)
    )
      return;
    if (
      !data.messages.some(
        (message) =>
          !message.deleted &&
          message.memberId !== memberId &&
          BigInt(message.noticeSeq) > BigInt(previous),
      )
    )
      return;
    toast(`New message in ${data.title}`, {
      id: "handler-new-message",
      action: {
        label: "Open chat",
        onClick: () => useChatStore.getState().setOpen(true),
      },
    });
  }, [data, memberId, open, documentVisible]);

  return {
    atLatest,
    setAtLatest,
    transcript,
    bottom,
    jumpToLatest,
    jumping,
    scrollRevision,
  };
}
